import React, { useState, useRef } from 'react';
import { FolderType, LinkType } from '../types';
import RichTextEditor from './RichTextEditor';
import { v4 as uuidv4 } from 'uuid';
import {
  ChevronDown,
  MessageCircle,
  FileText,
  PaintBucket,
  Pencil,
  Pin,
  Copy,
  Trash2,
  Plus,
  X,
} from 'lucide-react';

interface FolderItemProps {
  folder: FolderType;
  folders: FolderType[];
  links: LinkType[];
  onFoldersChange: (folders: FolderType[]) => void;
  onLinksChange: (links: LinkType[]) => void;
  isMobile?: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onDelete: (id: string) => void;
}

const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  folders,
  links,
  onFoldersChange,
  onLinksChange,
  isMobile,
  selectedId,
  onSelect,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [localColor, setLocalColor] = useState(folder.color);
  const [isDragging, setIsDragging] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState({ title: '', url: '', description: '', status: 'pending' as LinkType['status'], tags: '' });
  const [dragOverLinkId, setDragOverLinkId] = useState<string | null>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  const isSelected = selectedId === folder.id;
  const folderLinks = links.filter((l) => l.folderIds.includes(folder.id));

  const handleDragStart = (e: React.DragEvent) => {
    if (folder.isPinned) { e.preventDefault(); return; }
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ type: 'folder', id: folder.id })
    );
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleTitleChange = (title: string) => {
    onFoldersChange(folders.map((f) => (f.id === folder.id ? { ...f, title } : f)));
  };

  const handleColorChange = (color: string) => {
    setLocalColor(color);
    onFoldersChange(folders.map((f) => (f.id === folder.id ? { ...f, color } : f)));
  };

  const handlePinToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFoldersChange(
      folders.map((f) => (f.id === folder.id ? { ...f, isPinned: !f.isPinned } : f))
    );
  };

  const handleLinkDropInFolder = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;
    const item = JSON.parse(data);

    if (item.type === 'link') {
      onLinksChange(
        links.map((l) =>
          l.id === item.id
            ? { ...l, folderIds: Array.from(new Set([...l.folderIds, folder.id])) }
            : l
        )
      );
    } else if (item.type === 'link-from-folder' && item.folderId !== folder.id) {
      onLinksChange(
        links.map((l) =>
          l.id === item.id
            ? {
                ...l,
                folderIds: [...l.folderIds.filter((id) => id !== item.folderId), folder.id],
              }
            : l
        )
      );
    }
  };

  const handleAddFolderTag = (e: React.FormEvent) => {
    e.preventDefault();
    if (tagInput.trim() && !folder.tags.includes(tagInput.trim())) {
      onFoldersChange(
        folders.map((f) =>
          f.id === folder.id ? { ...f, tags: [...f.tags, tagInput.trim()] } : f
        )
      );
      setTagInput('');
    }
  };

  const handleRemoveFolderTag = (tagToRemove: string) => {
    onFoldersChange(
      folders.map((f) =>
        f.id === folder.id ? { ...f, tags: f.tags.filter((t) => t !== tagToRemove) } : f
      )
    );
  };

  const handleAddLinkToFolder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLinkUrl.trim()) return;
    let url = newLinkUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }
    let favicon = '';
    try {
      const u = new URL(url);
      favicon = `${u.protocol}//${u.hostname}/favicon.ico`;
    } catch {}
    const newLink: LinkType = {
      id: uuidv4(),
      title: '',
      url,
      description: '',
      favicon,
      tags: [],
      status: 'approved',
      position: { x: 0, y: 0 },
      folderIds: [folder.id],
      createdBy: '',
      createdAt: Date.now(),
    };
    onLinksChange([...links, newLink]);
    setNewLinkUrl('');
    setShowAddLink(false);
  };

  // ──────────────────────────────────────────────────
  // Default view — folder name + first 2 links + chevron
  // ──────────────────────────────────────────────────
  const previewLinks = folderLinks.slice(0, 2);
  const hasMore = folderLinks.length > 2;
  const collapsedView = (
    <div className="font-heebo select-none">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 truncate flex-1 min-w-0">
          <input
            ref={titleRef}
            className="bg-transparent border-none text-xl font-bold outline-none truncate flex-1 text-gray-800"
            value={folder.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="שם התיקייה..."
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          />
          <ChevronDown
            size={16}
            className="text-gray-600 cursor-pointer flex-shrink-0 transition-transform"
            style={{ transform: 'rotate(-90deg)' }}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
          />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {folder.isPinned && <Pin size={12} className="text-yellow-600" fill="currentColor" />}
          <span className="text-[10px] text-gray-400 font-bold">{folderLinks.length}</span>
        </div>
      </div>
      {previewLinks.length > 0 && (
        <div className="pr-6 space-y-0.5">
          {previewLinks.map((l) => (
            <div key={l.id} className="flex items-center gap-1 text-[11px] text-gray-500 truncate">
              {l.favicon && (
                <img src={l.favicon} alt="" className="w-3 h-3 object-contain flex-shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
              <span className="truncate">{l.title || l.url}</span>
            </div>
          ))}
          {hasMore && (
            <span className="text-[10px] text-gray-400">ועוד {folderLinks.length - 2}...</span>
          )}
        </div>
      )}
    </div>
  );

  // ──────────────────────────────────────────────────
  // Expanded view — toolbar + content area
  // ──────────────────────────────────────────────────
  const expandedView = (
    <div className="font-heebo select-none">
      {/* Top Toolbar — light gray bg, outline icons */}
      <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-100 border border-solid border-gray-300 border-b-0">
        <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 hover:text-black" title="כווץ">
          <ChevronDown size={14} />
        </button>
        <div className="w-px h-4 bg-gray-300" />
        <button
          onClick={(e) => { e.stopPropagation(); setShowComments(!showComments); }}
          className={`text-gray-600 hover:text-black ${showComments ? 'text-black' : ''}`}
          title="תגובות צוות"
        >
          <MessageCircle size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setShowNote(!showNote); }}
          className={`text-gray-600 hover:text-black ${showNote ? 'text-black' : ''}`}
          title="פתק מעוצב"
        >
          <FileText size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setShowColorPicker(!showColorPicker); }}
          className={`text-gray-600 hover:text-black ${showColorPicker ? 'text-black' : ''}`}
          title="צבע תיקייה"
        >
          <PaintBucket size={14} />
        </button>
        <div className="w-px h-4 bg-gray-300" />
        <button
            onClick={(e) => {
              e.stopPropagation();
              setTimeout(() => titleRef.current?.focus(), 0);
            }}
          className="text-gray-600 hover:text-black"
          title="ערוך שם תיקייה"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={handlePinToggle}
          className={`text-gray-600 hover:text-black ${folder.isPinned ? 'text-yellow-700' : ''}`}
          title="נעץ תיקייה"
        >
          <Pin size={14} />
        </button>
      </div>

      {/* Color picker */}
      {showColorPicker && (
        <div className="p-2 border border-solid border-gray-300 border-t-0 bg-gray-50 flex gap-1.5 flex-wrap items-center">
          {['#e2e8f0', '#fecaca', '#fed7aa', '#fef08a', '#bbf7d0', '#bfdbfe', '#c7d2fe', '#f0abfc', '#ffffff'].map(
            (c) => (
              <button
                key={c}
                className="w-5 h-5 border border-solid border-gray-400 hover:scale-105 transition-transform"
                style={{ backgroundColor: c }}
                onClick={() => handleColorChange(c)}
              />
            )
          )}
          <div className="flex items-center gap-1 mr-auto">
            <span className="text-[10px] font-bold text-gray-500">מותאם:</span>
            <input
              type="color"
              value={localColor}
              onChange={(e) => handleColorChange(e.target.value)}
              className="w-6 h-6 p-0 border border-solid border-gray-400 cursor-pointer"
            />
            <input
              type="text"
              value={localColor}
              onChange={(e) => {
                let val = e.target.value;
                if (val.startsWith('#')) {
                  if (/^#[0-9a-fA-F]{0,6}$/.test(val)) {
                    setLocalColor(val);
                  }
                } else {
                  if (/^[0-9a-fA-F]{0,6}$/.test(val)) {
                    setLocalColor('#' + val);
                  }
                }
              }}
              onBlur={() => {
                const hex = /^#[0-9a-fA-F]{6}$/.test(localColor) ? localColor : null;
                if (hex) {
                  handleColorChange(hex);
                } else {
                  setLocalColor(folder.color || '#e2e8f0');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const hex = /^#[0-9a-fA-F]{6}$/.test(localColor) ? localColor : null;
                  if (hex) {
                    handleColorChange(hex);
                  }
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="w-16 px-1 py-0.5 border border-solid border-gray-400 text-[10px] outline-none bg-white font-mono"
              placeholder="#HEX"
              dir="ltr"
            />
          </div>
        </div>
      )}

      {/* Main Content Area — white bg, light blue border */}
      <div
        className="bg-white p-3 relative"
        style={{ border: '1px solid #bfdbfe' }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleLinkDropInFolder}
      >
        {/* Folder name — top right */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1" />
          <input
            ref={titleRef}
            className="text-right bg-transparent border-none text-xl font-bold outline-none text-gray-800"
            value={folder.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            onBlur={() => {}}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            placeholder="שם התיקייה..."
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {/* Links list */}
        <div
          className="space-y-1 min-h-[40px]"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!folderLinks.length) setDragOverLinkId('__list__');
          }}
          onDragLeave={() => setDragOverLinkId(null)}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOverLinkId(null);
            const data = e.dataTransfer.getData('application/json');
            if (!data) return;
            const item = JSON.parse(data);
            if (item.type === 'link-from-folder' && item.folderId === folder.id) {
              const draggedLink = links.find((link) => link.id === item.id);
              if (!draggedLink) return;
              const newLinks = links.filter((link) => link.id !== item.id);
              newLinks.push(draggedLink);
              onLinksChange(newLinks);
            }
          }}
        >
          {folderLinks.length === 0 && !showAddLink ? (
            <div
              className="text-[11px] text-gray-400 text-center py-3 border border-dashed border-gray-300 cursor-pointer hover:bg-gray-50"
              onClick={() => setShowAddLink(true)}
            >
              <Plus size={12} className="inline mr-1" />
              הוסיפי לינק או גררי לכאן
            </div>
          ) : (
            folderLinks.map((l) => (
              editingLinkId === l.id ? (
                <div
                  key={l.id}
                  className="text-xs p-2 border border-solid border-gray-300 bg-gray-50 space-y-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    className="w-full px-2 py-1 border border-solid border-black text-xs outline-none bg-white"
                    placeholder="כותרת הלינק..."
                    value={editingData.title}
                    onChange={(e) => setEditingData({ ...editingData, title: e.target.value })}
                    dir="rtl"
                  />
                  <input
                    type="text"
                    className="w-full px-2 py-1 border border-solid border-black text-xs outline-none bg-white"
                    placeholder="כתובת URL..."
                    value={editingData.url}
                    onChange={(e) => setEditingData({ ...editingData, url: e.target.value })}
                    dir="ltr"
                  />
                  <input
                    type="text"
                    className="w-full px-2 py-1 border border-solid border-black text-xs outline-none bg-white"
                    placeholder="תיאור קצר..."
                    value={editingData.description}
                    onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                    dir="rtl"
                  />
                  <div className="flex items-center gap-2">
                    <select
                      className="border border-solid border-black text-[10px] px-1 py-0.5 bg-white outline-none"
                      value={editingData.status}
                      onChange={(e) => setEditingData({ ...editingData, status: e.target.value as typeof l.status })}
                    >
                      <option value="pending">ממתין</option>
                      <option value="in-progress">בתהליך</option>
                      <option value="approved">מאושר</option>
                      <option value="archived">בארכיון</option>
                    </select>
                    <input
                      type="text"
                      className="flex-1 px-2 py-1 border border-solid border-black text-[10px] outline-none bg-white"
                      placeholder="תגיות (מופרדות בפסיק)"
                      value={editingData.tags}
                      onChange={(e) => setEditingData({ ...editingData, tags: e.target.value })}
                      dir="rtl"
                    />
                  </div>
                  <div className="flex gap-1 pt-1">
                    <button
                      onClick={() => {
                        const newTags = editingData.tags.split(',').map((t) => t.trim()).filter(Boolean);
                        onLinksChange(
                          links.map((linkItem) =>
                            linkItem.id === l.id
                              ? {
                                  ...linkItem,
                                  title: editingData.title,
                                  url: editingData.url,
                                  description: editingData.description,
                                  status: editingData.status,
                                  tags: newTags,
                                }
                              : linkItem
                          )
                        );
                        setEditingLinkId(null);
                        setEditingData({ title: '', url: '', description: '', status: 'pending', tags: '' });
                      }}
                      className="px-2 py-0.5 bg-blue-600 text-white text-[10px] hover:bg-blue-700"
                    >
                      שמור
                    </button>
                    <button
                      onClick={() => {
                        setEditingLinkId(null);
                        setEditingData({ title: '', url: '', description: '', status: 'pending', tags: '' });
                      }}
                      className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] hover:bg-gray-300"
                    >
                      בטל
                    </button>
                  </div>
                </div>
              ) : (
              <div
                key={l.id}
      draggable={!folder.isPinned}
                onDragStart={(e) => {
                  e.stopPropagation();
                  e.dataTransfer.setData(
                    'application/json',
                    JSON.stringify({ type: 'link-from-folder', id: l.id, folderId: folder.id })
                  );
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverLinkId(l.id);
                }}
                onDragLeave={() => setDragOverLinkId(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverLinkId(null);
                  const data = e.dataTransfer.getData('application/json');
                  if (!data) return;
                  const item = JSON.parse(data);
                  if (item.type === 'link-from-folder' && item.folderId === folder.id && item.id !== l.id) {
                    const newLinks = links.filter((link) => link.id !== item.id);
                    const targetIdx = newLinks.findIndex((link) => link.id === l.id);
                    const draggedLink = links.find((link) => link.id === item.id)!;
                    newLinks.splice(targetIdx, 0, draggedLink);
                    onLinksChange(newLinks);
                  }
                }}
                className={`text-xs p-1.5 border border-solid bg-white hover:bg-gray-50 cursor-grab active:cursor-grabbing ${
                  dragOverLinkId === l.id ? 'border-blue-500' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 truncate flex-1 min-w-0">
                    {l.favicon && (
                      <img
                        src={l.favicon}
                        alt=""
                        className="w-3.5 h-3.5 object-contain flex-shrink-0"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    {l.url ? (
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium truncate text-blue-700 underline hover:text-blue-900"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {l.title || l.url}
                      </a>
                    ) : (
                      <span className="font-medium truncate text-gray-500">{l.title || 'ללא שם'}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingData({
                          title: l.title || '',
                          url: l.url || '',
                          description: l.description || '',
                          status: l.status || 'pending',
                          tags: (l.tags || []).join(', '),
                        });
                        setEditingLinkId(l.id);
                      }}
                      className="text-gray-400 hover:text-blue-600"
                      title="ערוך לינק"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newLink: LinkType = {
                          ...l,
                          id: uuidv4(),
                          title: `${l.title} (עותק)`,
                          createdAt: Date.now(),
                        };
                        onLinksChange([...links, newLink]);
                      }}
                      className="text-gray-400 hover:text-green-600"
                      title="צור עותק"
                    >
                      <Copy size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLinksChange(
                          links.map((linkItem) =>
                            linkItem.id === l.id
                              ? { ...linkItem, folderIds: linkItem.folderIds.filter((id) => id !== folder.id) }
                              : linkItem
                          )
                        );
                      }}
                      className="text-gray-400 hover:text-red-500"
                      title="הסר מהתיקייה"
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
                {l.description && (
                  <div className="text-[10px] text-gray-500 truncate pr-5 mt-0.5" dir="rtl">
                    {l.description}
                  </div>
                )}
              </div>
              )
            ))
          )}
        </div>

        {/* Trash + Tags + Add Link — bottom left */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('האם למחוק תיקייה זו? הקישורים עצמם לא יימחקו.')) {
                  onDelete(folder.id);
                }
              }}
              className="text-gray-400 hover:text-red-500"
              title="מחק תיקייה"
            >
              <Trash2 size={14} />
            </button>
            <div className="flex flex-wrap gap-1 items-center">
              {folder.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] bg-gray-100 text-gray-700 border border-solid border-gray-300 px-1 py-0.5 flex items-center gap-1"
                >
                  #{tag}
                  <button onClick={() => handleRemoveFolderTag(tag)} className="hover:text-red-500 font-bold">
                    <X size={10} />
                  </button>
                </span>
              ))}
              <form onSubmit={handleAddFolderTag} className="inline-flex items-center">
                <input
                  type="text"
                  placeholder="+תגית"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  className="text-[10px] px-1 py-0.5 border border-solid border-gray-300 outline-none w-14 focus:w-20 bg-white transition-all"
                  dir="rtl"
                  onClick={(e) => e.stopPropagation()}
                />
              </form>
            </div>
            <div className="w-px h-4 bg-gray-300" />
            {showAddLink ? (
              <form onSubmit={handleAddLinkToFolder} className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  className="w-24 bg-white border border-solid border-gray-400 p-0.5 text-[10px] outline-none"
                  placeholder="הדבק URL..."
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  dir="ltr"
                  autoFocus
                />
                <button type="submit" className="text-gray-600 hover:text-black border border-solid border-gray-400 px-0.5">
                  <Plus size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddLink(false); setNewLinkUrl(''); }}
                  className="text-gray-400 hover:text-red-500 border border-solid border-gray-400 px-0.5"
                >
                  <X size={11} />
                </button>
              </form>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); setShowAddLink(true); }}
                className="text-[10px] px-1 py-0.5 border border-dashed border-gray-400 text-gray-500 hover:bg-gray-50 hover:text-black font-bold"
              >
                +לינק
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Rich Text Note panel */}
      {showNote && (
        <div className="mt-1" onClick={(e) => e.stopPropagation()}>
          <RichTextEditor
            value={folder.richTextNote}
            onChange={(val) =>
              onFoldersChange(
                folders.map((f) => (f.id === folder.id ? { ...f, richTextNote: val } : f))
              )
            }
            placeholder="כתבו כאן הערות, תוכניות או תיעוד מעוצב..."
          />
        </div>
      )}

      {/* Comments panel */}
      {showComments && (
        <div className="border border-solid border-gray-300 border-t-0 p-2 bg-white" onClick={(e) => e.stopPropagation()}>
          <div className="text-xs font-bold text-gray-700 mb-1.5 flex items-center gap-1">
            <MessageCircle size={12} /> דיון צוותי
          </div>
          <div className="max-h-40 overflow-y-auto space-y-2 mb-2 bg-gray-50 border border-solid border-gray-200 p-2">
            {folder.comments.length === 0 ? (
              <div className="text-[11px] text-gray-400 text-center py-2">אין תגובות עדיין.</div>
            ) : (
              folder.comments.map((c) => (
                <div key={c.id} className="text-[11px] text-black border-r-2 border-solid border-pink-500 pr-1.5 py-0.5">
                  <div className="flex justify-between items-center text-[10px] text-gray-500 font-bold mb-0.5">
                    <span>{c.userName}</span>
                    <span>{new Date(c.createdAt).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div className="break-words">{c.text}</div>
                </div>
              ))
            )}
          </div>
          <input
            className="w-full p-1.5 text-xs border border-solid border-gray-300 bg-white outline-none"
            placeholder="הקלד תגובה ולחץ Enter..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                const text = (e.target as HTMLInputElement).value.trim();
                const newComment = {
                  id: Date.now().toString(),
                  userId: '',
                  userName: folder.createdBy || 'חבר צוות',
                  text,
                  createdAt: Date.now(),
                };
                onFoldersChange(
                  folders.map((f) =>
                    f.id === folder.id ? { ...f, comments: [...f.comments, newComment] } : f
                  )
                );
                (e.target as HTMLInputElement).value = '';
              }
            }}
            dir="rtl"
          />
        </div>
      )}
    </div>
  );

  // ──────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────
  const renderContent = isOpen ? expandedView : collapsedView;

  if (isMobile) {
    return (
      <div
        onClick={() => onSelect(folder.id)}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleLinkDropInFolder}
        className={`p-3 bg-white transition-all ${
          isSelected ? 'border-2 border-pink-500' : ''
        }`}
        style={{
          border: isSelected ? '2px solid #ec4899' : `10px solid ${folder.color || localColor}`,
        }}
      >
        {renderContent}
      </div>
    );
  }

  return (
    <div
      ref={dragRef}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={handleLinkDropInFolder}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(folder.id);
      }}
      className={`absolute p-3 cursor-grab active:cursor-grabbing bg-white transition-all ${
        isSelected ? 'border-2 border-pink-500' : ''
      } ${isDragging ? 'opacity-70 z-[9999]' : ''}`}
      style={{
        left: folder.position.x,
        top: folder.position.y,
        minWidth: isOpen ? 280 : 220,
        maxWidth: isOpen ? 360 : 300,
        zIndex: isDragging ? 9999 : isSelected ? 30 : folder.isPinned ? 20 : 10,
        border: isSelected ? '2px solid #ec4899' : `10px solid ${folder.color || localColor}`,
      }}
    >
      {renderContent}
    </div>
  );
};

export default FolderItem;